/**
 * Dates, as the app means them: a calendar day, not an instant.
 *
 * A run belongs to a DAY. "Today" is the day the operator is looking at
 * the screen, not the day it is in UTC. `toISOString()` converts to UTC
 * FIRST, so for 12 of every 24 hours in New Zealand it returns YESTERDAY
 * - a local midnight becomes the previous day once it crosses the
 * date line going west.
 *
 * Every date this app sends or compares is built from these helpers.
 */

/**
 * A Date as YYYY-MM-DD, in LOCAL time.
 *
 * This is the only way to turn a Date into a date key.
 */
export const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Today, in local time, as YYYY-MM-DD.
 */
export const todayKey = () => formatDateInput(new Date());

/**
 * The date part of a date string, whatever shape it arrives in.
 *
 * The API returns DATE columns as a full ISO datetime
 * ("2026-10-30T00:00:00.000000Z"), so anything that compares or
 * displays it has to take the date part first.
 */
export const dateKey = (dateString) => String(dateString || '').slice(0, 10);

/**
 * A date key, shifted by whole days.
 *
 * The arithmetic is done on the DATE, not on an instant: "the day after
 * 2026-03-28" is 2026-03-29 whatever the timezone and whatever the
 * clock says. Building the Date at local noon avoids the midnight edge
 * cases, and shifting by milliseconds avoids a daylight-saving
 * boundary landing on the wrong day.
 */
export const addDays = (dateKeyString, days) => {
  const [y, m, d] = dateKey(dateKeyString).split('-').map(Number);
  if (!y || !m || !d) return dateKeyString;

  // Noon local time: far enough from either midnight to be safe.
  const date = new Date(y, m - 1, d, 12, 0, 0, 0);
  date.setDate(date.getDate() + days);

  return formatDateInput(date);
};

/**
 * The weekday of a date key, as a short name.
 */
export const weekdayOf = (dateKeyString) => {
  const [y, m, d] = dateKey(dateKeyString).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d, 12, 0, 0, 0).toLocaleDateString('en-NZ', {
    weekday: 'long',
  });
};
